import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase-client";
import {
  Page,
  LegacyCard,
  IndexTable,
  Text,
  Spinner,
  Badge,
  Button,
  Modal,
  BlockStack,
  TextField,
  Select,
  Banner,
} from "@shopify/polaris";
import { formatPrice, formatFriendlyDate, formatPlate } from "../../utility/funcs";
import { UserAuth } from "../../context/AuthContext";

// Unified queue of pending bank (SPEI) transfers, covering two sources:
//   - Deposit returns: reservations with deposit_returned=true but
//     deposit_transferred=false. The returned money has no outflow transaction,
//     so marking it transferred records a cash_movements expense.
//   - Refunds: transactions of type='refund' with transferred=false (a SPEI
//     refund awaiting send). The refund transaction itself IS the outflow and
//     is already counted as an expense by the cash dashboard, so marking it
//     transferred must NOT insert a cash_movements row — it just finalizes the
//     sending bank account and flips the flag.

function PendingDepositTransfers() {
  const navigate = useNavigate();
  const { session } = UserAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [transferAccount, setTransferAccount] = useState("");
  const [transferRef, setTransferRef] = useState("");
  const [saving, setSaving] = useState(false);
  const [bankModal, setBankModal] = useState(null); // normalized row or null
  const [copiedField, setCopiedField] = useState("");

  const copyToClipboard = async (text, field) => {
    if (!text) return;
    try {
      // Strip whitespace so pasting into a bank form drops cleanly.
      await navigator.clipboard.writeText(String(text).replace(/\s+/g, ""));
      setCopiedField(field);
      setTimeout(() => setCopiedField(""), 1500);
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);

    // --- Pending deposit returns (source: reservations) ---
    const { data: depRows } = await supabase
      .from("reservations")
      .select(
        "id, deposit, deposit_amount_returned, deposit_returned, deposit_returned_at, deposit_transferred, status, customer(id, first_name, last_name, bank_name, bank_clabe, bank_holder_name), company:companies(id, name, bank_name, bank_clabe, bank_holder_name), car(plate, model(name))"
      )
      .eq("type", "rental")
      .eq("deposit_returned", true)
      .eq("deposit_transferred", false)
      .gt("deposit", 0)
      .order("deposit_returned_at", { ascending: true });

    // Original deposit account name per reservation (shown as "Cuenta Origen").
    const depIds = (depRows || []).map((r) => r.id);
    const depositAccountMap = {};
    if (depIds.length) {
      const { data: deposits } = await supabase
        .from("transactions")
        .select("reservation, account(name)")
        .eq("isDeposit", true)
        .in("reservation", depIds);
      (deposits || []).forEach((d) => {
        depositAccountMap[d.reservation] = d.account?.name || null;
      });
    }

    const depositRows = (depRows || []).map((r) => {
      const amount =
        r.deposit_amount_returned != null
          ? r.deposit_amount_returned
          : r.deposit;
      return {
        kind: "deposit",
        key: `deposit-${r.id}`,
        reservationId: r.id,
        company: r.company,
        customer: r.customer,
        car: r.car,
        amount,
        partialNote:
          r.deposit_amount_returned != null &&
          r.deposit_amount_returned !== r.deposit
            ? `de ${formatPrice(r.deposit)}`
            : null,
        sourceAccountName: depositAccountMap[r.id] || "-",
        date: r.deposit_returned_at,
      };
    });

    // --- Pending refund transfers (source: transactions type=refund) ---
    const { data: refundTx } = await supabase
      .from("transactions")
      .select(
        "id, amount, payment_date, created_at, account, reservation, billing_entity_type, billing_entity_id"
      )
      .eq("type", "refund")
      .eq("transferred", false)
      .order("created_at", { ascending: true });

    const refundResIds = [
      ...new Set((refundTx || []).map((t) => t.reservation).filter(Boolean)),
    ];
    const refundResMap = {};
    if (refundResIds.length) {
      const { data: refundRes } = await supabase
        .from("reservations")
        .select(
          "id, customer(id, first_name, last_name, bank_name, bank_clabe, bank_holder_name), company:companies(id, name, bank_name, bank_clabe, bank_holder_name), car(plate, model(name))"
        )
        .in("id", refundResIds);
      (refundRes || []).forEach((r) => {
        refundResMap[r.id] = r;
      });
    }

    // Account names (for the refund "Cuenta Origen" — the bank chosen at refund
    // time, finalized when transferred).
    const accountNameMap = {};
    {
      const { data: accts } = await supabase
        .from("transaction_accounts")
        .select("id, name");
      (accts || []).forEach((a) => {
        accountNameMap[a.id] = a.name;
      });
    }

    // Credit refunds (from a client's profile) carry no reservation, so the
    // payee has to come off the transaction's billing entity instead.
    const creditRefunds = (refundTx || []).filter(
      (t) => !t.reservation && t.billing_entity_id
    );
    const entityMap = { customer: {}, company: {} };
    for (const [type, table, cols] of [
      ["customer", "customers", "id, first_name, last_name, bank_name, bank_clabe, bank_holder_name"],
      ["company", "companies", "id, name, bank_name, bank_clabe, bank_holder_name"],
    ]) {
      const ids = [
        ...new Set(
          creditRefunds
            .filter((t) => t.billing_entity_type === type)
            .map((t) => t.billing_entity_id)
        ),
      ];
      if (!ids.length) continue;
      const { data } = await supabase.from(table).select(cols).in("id", ids);
      (data || []).forEach((r) => {
        entityMap[type][r.id] = r;
      });
    }

    const refundRows = (refundTx || []).map((t) => {
      const res = refundResMap[t.reservation] || {};
      const entity = t.reservation
        ? null
        : entityMap[t.billing_entity_type]?.[t.billing_entity_id];
      return {
        kind: "refund",
        key: `refund-${t.id}`,
        transactionId: t.id,
        reservationId: t.reservation,
        company: res.company || (t.billing_entity_type === "company" ? entity : null),
        customer: res.customer || (t.billing_entity_type === "customer" ? entity : null),
        car: res.car,
        amount: t.amount,
        partialNote: null,
        sourceAccountName: accountNameMap[t.account] || "-",
        date: t.payment_date || t.created_at,
      };
    });

    // Merge and sort by date (oldest first). Drop any $0 (or negative) rows:
    // there is no money to send, so they should never appear as a pending
    // transfer (guards against legacy rows and $0 refunds).
    const merged = [...depositRows, ...refundRows]
      .filter((r) => Number(r.amount) > 0)
      .sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return da - db;
    });

    setRows(merged);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    const fetchAccounts = async () => {
      const { data: accts } = await supabase
        .from("transaction_accounts")
        .select("id, name")
        .eq("type", "bank")
        // Clip and Fintoc feed themselves; see transaction_accounts.manual_entry.
        .eq("manual_entry", true)
        .order("name");
      if (accts) {
        setAccounts(
          accts.map((a) => ({ value: String(a.id), label: a.name }))
        );
      }
    };
    fetchAccounts();
  }, []);

  const displayName = (row) =>
    row.company?.name ||
    (row.customer
      ? `${row.customer.first_name} ${row.customer.last_name}`
      : "-");

  const openConfirm = (row) => {
    setSelected(row);
    setTransferAccount("");
    setTransferRef("");
    setModalOpen(true);
  };

  const confirmTransfer = async () => {
    if (!selected) return;
    setSaving(true);

    const transferAmount = selected.amount;
    const accountLabel =
      accounts.find((a) => a.value === transferAccount)?.label || "";
    const now = new Date().toISOString();

    if (selected.kind === "deposit") {
      await supabase
        .from("reservations")
        .update({ deposit_transferred: true, deposit_transferred_at: now })
        .eq("id", selected.reservationId);

      // Deposit returns create no outflow transaction, so the SPEI outflow is
      // recorded here in Caja Diaria.
      await supabase.from("cash_movements").insert({
        type: "expense",
        amount: transferAmount,
        description: `Devolución de depósito SPEI - Reserva #${selected.reservationId}${
          transferRef ? ` (Ref: ${transferRef})` : ""
        }`,
        account_id: parseInt(transferAccount),
        reservation_id: selected.reservationId,
        created_by: session?.user?.id,
      });

      await supabase.from("reservation_audit_log").insert({
        reservation_id: selected.reservationId,
        user_id: session?.user?.id,
        action: "deposit_transferred",
        details: `Depósito de ${formatPrice(transferAmount)} transferido vía SPEI${
          accountLabel ? ` desde cuenta ${accountLabel}` : ""
        }${transferRef ? ` — Ref: ${transferRef}` : ""}`,
      });
    } else {
      // Refund: the refund transaction is already the outflow (counted by the
      // cash dashboard), so do NOT insert a cash_movements row. Flip the flag
      // and finalize the bank the transfer actually went out from.
      await supabase
        .from("transactions")
        .update({
          transferred: true,
          transferred_at: now,
          account: parseInt(transferAccount),
        })
        .eq("id", selected.transactionId);

      // Credit refunds come off a client profile, not a reservation, so there
      // is no reservation to log against (reservation_id is NOT NULL).
      if (selected.reservationId) {
        await supabase.from("reservation_audit_log").insert({
          reservation_id: selected.reservationId,
          user_id: session?.user?.id,
          action: "refund_transferred",
          details: `Reembolso de ${formatPrice(transferAmount)} transferido vía SPEI${
            accountLabel ? ` desde cuenta ${accountLabel}` : ""
          }${transferRef ? ` — Ref: ${transferRef}` : ""}`,
        });
      }
    }

    setSaving(false);
    setModalOpen(false);
    setSelected(null);
    fetchData();
  };

  return (
    <AppWrapper>
      <Page
        title="Transferencias Pendientes"
        subtitle="Depósitos devueltos y reembolsos que aún no se han transferido vía SPEI"
      >
        {loading ? (
          <Spinner size="small" />
        ) : rows.length === 0 ? (
          <Banner tone="success">
            <p>No hay transferencias pendientes.</p>
          </Banner>
        ) : (
          <LegacyCard>
            <IndexTable
              resourceName={{
                singular: "Transferencia",
                plural: "Transferencias",
              }}
              selectable={false}
              itemCount={rows.length}
              headings={[
                { title: "#" },
                { title: "Tipo" },
                { title: "Cliente" },
                { title: "Auto" },
                { title: "Monto" },
                { title: "Cuenta Origen" },
                { title: "Fecha" },
                { title: "" },
              ]}
            >
              {rows.map((r, i) => (
                <IndexTable.Row key={r.key} id={r.key} position={i}>
                  <IndexTable.Cell>
                    {r.reservationId ? (
                      <Button
                        variant="plain"
                        onClick={() =>
                          navigate(`/reservation?id=${r.reservationId}`)
                        }
                      >
                        #{r.reservationId}
                      </Button>
                    ) : (
                      <Text tone="subdued">Crédito</Text>
                    )}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {r.kind === "deposit" ? (
                      <Badge tone="info">Depósito</Badge>
                    ) : (
                      <Badge tone="attention">Reembolso</Badge>
                    )}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div
                      style={{
                        width: 160,
                        maxWidth: 160,
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBankModal(r);
                      }}
                      title="Ver datos bancarios"
                    >
                      <BlockStack gap="050">
                        {r.company?.name && (
                          <Text
                            variant="bodySm"
                            fontWeight="semibold"
                            truncate
                          >
                            {r.company.name}
                          </Text>
                        )}
                        {r.customer && (
                          <Text
                            variant="bodySm"
                            tone={r.company?.name ? "subdued" : undefined}
                            truncate
                          >
                            {`${r.customer.first_name} ${r.customer.last_name}`}
                          </Text>
                        )}
                        {!r.company?.name && !r.customer && <Text>-</Text>}
                      </BlockStack>
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm">
                      {r.car?.model?.name} ({formatPlate(r.car?.plate)})
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <BlockStack gap="050">
                      <Text fontWeight="bold">{formatPrice(r.amount)}</Text>
                      {r.partialNote && (
                        <Text variant="bodySm" tone="subdued">
                          {r.partialNote}
                        </Text>
                      )}
                    </BlockStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm">{r.sourceAccountName}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text variant="bodySm" tone="subdued">
                      {r.date ? formatFriendlyDate(r.date) : "-"}
                    </Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Button
                      variant="primary"
                      size="slim"
                      onClick={() => openConfirm(r)}
                    >
                      Marcar Transferido
                    </Button>
                  </IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          </LegacyCard>
        )}
      </Page>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Confirmar Transferencia SPEI"
        primaryAction={{
          content: "Confirmar",
          onAction: confirmTransfer,
          loading: saving,
          disabled: !transferAccount,
        }}
        secondaryActions={[
          { content: "Cancelar", onAction: () => setModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {selected && (
              <Text>
                {selected.kind === "deposit" ? "Depósito" : "Reembolso"} —{" "}
                {selected.reservationId ? (
                  <>
                    Reserva <strong>#{selected.reservationId}</strong> —{" "}
                  </>
                ) : (
                  <>Crédito de cliente — </>
                )}
                {displayName(selected)} —{" "}
                <strong>{formatPrice(selected.amount)}</strong>
              </Text>
            )}
            <Select
              label="Cuenta de salida"
              options={[{ value: "", label: "Seleccionar" }, ...accounts]}
              value={transferAccount}
              onChange={setTransferAccount}
            />
            <TextField
              label="Referencia SPEI (opcional)"
              value={transferRef}
              onChange={setTransferRef}
              placeholder="Ej: 123456789"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={!!bankModal}
        onClose={() => setBankModal(null)}
        title="Datos bancarios"
        secondaryActions={(() => {
          const useCompany = !!bankModal?.company?.id;
          return [
            {
              content: useCompany ? "Ver empresa" : "Ver cliente",
              onAction: () => {
                if (useCompany)
                  navigate(`/companies/edit?id=${bankModal.company.id}`);
                else if (bankModal?.customer?.id)
                  navigate(`/customers/edit?id=${bankModal.customer.id}`);
              },
              disabled: useCompany
                ? !bankModal?.company?.id
                : !bankModal?.customer?.id,
            },
            { content: "Cerrar", onAction: () => setBankModal(null) },
          ];
        })()}
      >
        <Modal.Section>
          {bankModal &&
            (() => {
              // When the reservation is linked to a company, the money comes
              // back to the company account. Otherwise, the customer's.
              const useCompany = !!bankModal.company;
              const source = useCompany ? bankModal.company : bankModal.customer;
              const hasData =
                source?.bank_name ||
                source?.bank_clabe ||
                source?.bank_holder_name;
              const subjectLabel = useCompany ? "empresa" : "cliente";
              return (
                <BlockStack gap="300">
                  {bankModal.company?.name && (
                    <Text variant="bodyMd" fontWeight="semibold">
                      {bankModal.company.name}
                    </Text>
                  )}
                  {bankModal.customer && (
                    <Text variant="bodySm" tone="subdued">
                      {`${bankModal.customer.first_name || ""} ${bankModal.customer.last_name || ""}`.trim()}
                    </Text>
                  )}
                  {!hasData ? (
                    <Banner tone="warning">
                      <p>
                        Esta {subjectLabel} no tiene datos bancarios
                        registrados. Agrégalos desde la ficha de la{" "}
                        {subjectLabel} para tenerlos a la mano aquí.
                      </p>
                    </Banner>
                  ) : (
                    <BlockStack gap="200">
                      <div>
                        <Text variant="bodySm" tone="subdued">
                          CLABE interbancaria
                        </Text>
                        {source?.bank_clabe ? (
                          <Button
                            variant="plain"
                            onClick={() =>
                              copyToClipboard(source.bank_clabe, "clabe")
                            }
                          >
                            <span
                              style={{
                                fontFamily: "ui-monospace, monospace",
                                fontSize: "0.95rem",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {source.bank_clabe}
                            </span>
                            {copiedField === "clabe" && " ✓ Copiado"}
                          </Button>
                        ) : (
                          <Text>—</Text>
                        )}
                      </div>
                      <div>
                        <Text variant="bodySm" tone="subdued">
                          Banco
                        </Text>
                        <Text variant="bodyMd">
                          {source?.bank_name || "—"}
                        </Text>
                      </div>
                      <div>
                        <Text variant="bodySm" tone="subdued">
                          Titular de la cuenta
                        </Text>
                        <Text variant="bodyMd">
                          {source?.bank_holder_name || "—"}
                        </Text>
                      </div>
                    </BlockStack>
                  )}
                </BlockStack>
              );
            })()}
        </Modal.Section>
      </Modal>
    </AppWrapper>
  );
}

export default PendingDepositTransfers;
